/**
 * Hold the page still while something is open on top of it.
 *
 * Reported from an iPhone, with the rule panel up: "scrolling up and down controls the content
 * behind it instead of the content in the slideup." Two separate leaks produce that one feeling,
 * and both have to be plugged:
 *
 *   THE SHEET RUNS OUT AND HANDS THE GESTURE ON. A scroller at its top or bottom passes the rest
 *   of the swipe to its parent, all the way up to the document. That is `overscroll-behavior:
 *   contain`, in the stylesheet, on each overlay's scroller.
 *
 *   THE FINGER LANDS SOMEWHERE THAT IS NOT THE SCROLLER. A sheet's header, its footer, the gap
 *   between its sections — none of those scroll, so the swipe goes straight to the page behind.
 *   Nothing but locking the page fixes that, which is this file.
 *
 * `overflow: hidden` on the body is the usual advice and does not work on iOS Safari: the page
 * keeps scrolling, and after the sheet closes you are somewhere else. Pinning the body with
 * `position: fixed` at its current offset does work, at the cost of having to put the offset back
 * by hand — so the returned function is not optional and the scroll position is restored exactly.
 *
 * Ref-counted, because overlays stack: opening a rule from inside a confirm sheet must not
 * unpin the page when the rule closes and the confirm is still up.
 */

interface Saved {
  y: number;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  paddingRight: string;
}

let depth = 0;
let saved: Saved | null = null;

/** Width of the classic desktop scrollbar, so pinning the body does not shift the layout. */
function gutter(): number {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

export function lockScroll(): () => void {
  if (typeof document === 'undefined') return () => {};
  depth += 1;

  if (depth === 1) {
    const s = document.body.style;
    saved = {
      y: window.scrollY,
      position: s.position,
      top: s.top,
      left: s.left,
      right: s.right,
      width: s.width,
      paddingRight: s.paddingRight,
    };
    const pad = gutter();
    s.position = 'fixed';
    s.top = `${-saved.y}px`;
    s.left = '0';
    s.right = '0';
    s.width = '100%';
    if (pad > 0) s.paddingRight = `${pad}px`;
  }

  let released = false;
  return () => {
    // Guard the release: React can run a cleanup twice in StrictMode, and a double decrement
    // would unpin the page while another overlay is still open.
    if (released) return;
    released = true;
    depth -= 1;
    if (depth > 0 || !saved) return;
    const s = document.body.style;
    s.position = saved.position;
    s.top = saved.top;
    s.left = saved.left;
    s.right = saved.right;
    s.width = saved.width;
    s.paddingRight = saved.paddingRight;
    window.scrollTo(0, saved.y);
    saved = null;
  };
}

/** Test seam: the number of overlays currently holding the page. */
export function lockDepth(): number {
  return depth;
}
